import { create } from 'zustand';

import { AUTH_CONST } from '@/constant';
import { storage } from '@/package';
import type {
  CognitoStoreActions,
  CognitoStoreState,
  CognitoTokenInfo,
} from '@/type';

const {
  VITE_APP_CONTROL_PLANE_URL: CONTROL_PLANE_URL,
  VITE_APP_COGNITO_AUTH_URL: COGNITO_AUTH_URL,
  VITE_APP_COGNITO_CLIENT_ID: COGNITO_CLIENT_ID,
} = import.meta.env;

const INIT_STATE = {
  clientId: COGNITO_CLIENT_ID,
  redirectUri: new URL(import.meta.url).origin,
  url: COGNITO_AUTH_URL,
};

const processFetchResponse = async <T = any>(res: Response): Promise<T> => {
  switch (Math.floor(res.status / 100)) {
    case 4:
    case 5: {
      const data = await res.json();

      throw new Error(data.resultMsg, {
        cause: {
          url: res.url,
          status: res.status,
          statusText: res.statusText,
          data,
        },
      });
    }
    default:
      return (await res.json()).resultData;
  }
};

const useCognitoStore = create<CognitoStoreState & CognitoStoreActions>(
  (set, get) => {
    return {
      ...INIT_STATE,

      authenticateAccount({ email, password, newPassword }) {
        const data: { email: string; password: string; newPassword?: string } =
          {
            email,
            password,
          };

        newPassword && (data.newPassword = newPassword);

        return fetch(`${CONTROL_PLANE_URL}/token/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }).then<
          CognitoTokenInfo & {
            forceConfirmed: boolean;
            marketingPromotion: boolean;
          }
        >(processFetchResponse);
      },

      clear() {
        set(INIT_STATE);
      },

      getFeatureFlag(featureFlagKey: string) {
        return fetch(`${CONTROL_PLANE_URL}/config/${featureFlagKey}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(processFetchResponse);
      },

      getRegionList() {
        return fetch(`${CONTROL_PLANE_URL}/region`, {
          headers: {
            'Content-Type': 'application/json',
          },
        }).then(processFetchResponse);
      },

      getSignInUrl({ provider, scope, redirectUri }) {
        const clientID = get().clientId;

        if (!clientID) {
          throw new Error('Cognito client id is empty');
        }

        const searchParams = new URLSearchParams({
          access_type: 'offline',
          client_id: clientID,
          identity_provider: provider ?? 'Google',
          redirect_uri: redirectUri ?? get().redirectUri,
          response_type: 'code',
          scope: scope ?? 'openid',
          state: '',
        });

        return `${get().url}/oauth2/authorize?${searchParams}`;
      },

      getSignOutUrl() {
        const redirectUri = `${get().redirectUri}/auth/signin`;
        const searchParams = new URLSearchParams({
          client_id: get().clientId,
          redirect_uri: redirectUri,
          logout_uri: redirectUri,
          response_type: 'code',
        });

        return `${get().url}/logout?${searchParams}`;
      },

      getTokenFromCode(code, redirectURL) {
        return fetch(`${CONTROL_PLANE_URL}/token/code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            redirectURL: redirectURL ?? new URL(import.meta.url).origin,
          }),
        }).then(processFetchResponse);
      },

      refreshToken({ idToken, refreshToken }) {
        return fetch(`${CONTROL_PLANE_URL}/token/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            idToken,
            refreshToken,
          }),
        }).then(processFetchResponse);
      },

      resetPasswordRequest({ email }) {
        return fetch(`${CONTROL_PLANE_URL}/user/password/reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
          }),
        }).then(processFetchResponse);
      },

      checkVerifyPasswordChangeCode({ code, email, password }) {
        return fetch(`${CONTROL_PLANE_URL}/user/password/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            code,
            password,
          }),
        }).then(processFetchResponse);
      },

      signUpForQR({ tenantData, tenantRegistrationData }) {
        return fetch(`${CONTROL_PLANE_URL}/user/invite/qr`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tenantData: {
              registrationStatus: 'In Progress',
              ...tenantData,
            },
            tenantRegistrationData,
          }),
        }).then(processFetchResponse);
      },

      termsAgreements({
        termsOfService,
        personalInfoCollection,
        personalInfoTransfer,
        marketingPromotion,
        consentAITraining,
      }) {
        const idToken = storage.local.get(AUTH_CONST.KEYS.STORAGE_ID_TOKEN);

        return fetch(`${CONTROL_PLANE_URL}/user/terms/agree`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            personalInfoCollection,
            personalInfoTransfer,
            marketingPromotion,
            termsOfService,
            consentAITraining,
          }),
        }).then(processFetchResponse);
      },

      verifyGoogleCode({
        code,
        redirectUri = new URL(import.meta.url).origin + '/auth/signup/qr',
      }) {
        return fetch(`${import.meta.env.VITE_APP_API_URL}/verify`, {
          method: 'POST',
          body: JSON.stringify({
            code,
            redirectUri,
          }),
        })
          .then(res => res.json())
          .then(res => {
            if (res.success) return res.user;

            throw new Error(res.error ?? 'Exceptions');
          });
      },
    };
  },
);

export default useCognitoStore;
